import React, { useRef, useCallback } from 'react';
import styled from 'styled-components';
import { space, color } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import Text from 'theme/text';
import translations from 'translations';
import selectProfile from 'redux-api/reselect/profile';
import Input from './input';
import SelectRemote from './select-remote';

const StyledSelect = styled(SelectRemote)`
  & > .ant-select > .ant-select-selector {
    height: 38px;
    border-color: ${themeGet('colors.primary_100')};

    :hover {
      border-color: ${themeGet('colors.primary_200')};
    }

    :focus {
      border-color: ${themeGet('colors.primary_500')};
    }
  }

  & > .ant-select-single:not(.ant-select-customize-input) .ant-select-selector {
    height: 38px;
  }
`;

const WrapSelect = styled.div`
  position: relative;
`;

const LabelColor = styled.div`
  ${color}

  position: absolute;
  top: 2px;
  left: 2px;
  width: 10px;
  height: 34px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
`;

const Wrapper = styled(motion.div)`
  overflow: ${({ opened }) => (opened === 'true' ? 'visible' : 'hidden')};

  .container {
    ${color}
    ${space}

    border: 2px solid ${themeGet('colors.primary_200')};
    border-radius: 12px;

    & > .filter-head {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: ${themeGet('space.m')}px;
      padding-bottom: 0;

      & > .left {
        display: flex;
        flex-direction: row;
      }

      & > .right {
        .link {
          display: flex;
          align-items: center;
          text-decoration: none;
          cursor: pointer;
          color: ${themeGet('colors.primary_400')};

          & > :hover {
            color: ${themeGet('colors.primary_300')};
          }
        }
      }
    }

    & > .filter-body {
      display: flex;
      flex-direction: row;
      padding: ${themeGet('space.m')}px;
      flex-wrap: wrap;

      & > .wrap-input {
        width: 279px;
        padding: ${themeGet('space.xs')}px;
      }
    }
  }
`;

function MoreFilters({
  onReset,
  isOpen,
  languageCode,
  otherFilter,
  onChangeOtherFilter,
  onChangeTermFilter,
  dataType,
}) {
  const variants = useRef({
    visible: { opacity: 1, height: 'auto' },
    hidden: { opacity: 0, height: '0px' },
  });

  const handleChangeSelect = useCallback(
    (selected, isExclusive = false) => {
      if (selected.value !== '') {
        if (selected.type === 'order_type') {
          return onChangeTermFilter(selected.type, selected.label, selected.value, selected.color, isExclusive);
        }

        return onChangeOtherFilter(selected.type, selected.label, selected.value, selected.color, isExclusive);
      }

      return null;
    },
    [onChangeTermFilter, onChangeOtherFilter],
  );

  const handleInputSelect = useCallback(
    (selected, isExclusive = false) => {
      if (selected.value !== '') {
        if (selected.type === 'order_type') {
          return onChangeTermFilter(selected.type, selected.label, selected.value, selected.color, isExclusive);
        }

        return onChangeOtherFilter(selected.type, selected.label, selected.value, selected.color, isExclusive);
      }

      return null;
    },
    [onChangeTermFilter, onChangeOtherFilter],
  );

  const { isAdminAnalytics } = useSelector(selectProfile);

  return (
    <Wrapper
      mt="m"
      bg="white"
      initial="hidden"
      opened={isOpen.toString()}
      animate={isOpen ? 'visible' : 'hidden'}
      variants={variants.current}
      transition={{ ease: 'linear', duration: 0.6 }}
    >
      <div className="container">
        <div className="filter-head">
          <div className="left">
            <Text fontSize="h2" lineHeight="h2" fontWeight="bold" color="grey_600">
              {translations[languageCode].filters}
            </Text>
          </div>

          <div className="right">
            <button
              title={translations[languageCode].reset}
              type="button"
              className="link"
              onClick={onReset}
            >
              <Text fontSize="pLarge" lineHeight="pLarge" fontWeight="bold" color="grey_600">
                {translations[languageCode].reset}
              </Text>
            </button>
          </div>
        </div>

        <div className="filter-body">
          {otherFilter.map(
            (filter) =>
              (!filter.isAdmin || (isAdminAnalytics && filter.isAdmin)) && (
                <div key={filter.type} className="wrap-input">
                  <Text fontSize="display" lineHeight="display" fontWeight="bold" color="grey_600">
                    {filter.label}
                  </Text>
                  {!filter.endpoint ? (
                    <Input data={filter} meta={{}} onSelect={handleInputSelect} />
                  ) : (
                    <WrapSelect>
                      <StyledSelect
                        input={{
                          value: '',
                          onChange: handleChangeSelect,
                        }}
                        data={filter}
                        languageCode={languageCode}
                        meta={{}}
                        noError
                        dataType={dataType}
                      />
                      <LabelColor bg={filter.color || 'black'} />
                    </WrapSelect>
                  )}
                </div>
              ),
          )}
        </div>
      </div>
    </Wrapper>
  );
}

export default MoreFilters;
